import React from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Breadcrumb, SearchableSelect, Spinner } from '../../../shared/components';
import { formatDateTime } from '../../../shared/utils/localization';
import { useToast } from '../../notifications';
import { useAuth } from '../../auth/context/AuthContext';
import { PermissionScopes } from '../../auth/permissions';
import { getAssets } from '../../assets/services/assetService';
import type { Asset } from '../../assets/types/Asset';
import { listEvents } from '../../calendar/services/calendarService';
import type { AppointmentType, CalendarEvent } from '../../calendar/types/Calendar';
import { getContacts } from '../../contacts/services/contactService';
import type { Contact } from '../../contacts/types/Contact';
import {
  addConsultationFieldAssignment,
  addConsultationFieldValue,
  createConsultation,
  getConsultationTemplateById,
  listConsultationFieldDefinitions,
  listConsultationStatuses,
  listConsultationTemplates,
  listConsultationTypes,
  uploadConsultationFieldAttachment,
  uploadConsultationFieldVoice,
  transcribeConsultationFieldVoice,
} from '../services/consultationService';
import type {
  AddConsultationFieldAssignmentInput,
  ConsultationFieldDefinition,
  ConsultationStatusDefinition,
  ConsultationTemplate,
} from '../types/Consultation';
import { parseConsultationVoiceSchema } from '../consultationVoiceSchema';
import {
  buildStructuredConsultationFieldValues,
  getMissingRequiredConsultationFieldIds,
  getMissingRequiredConsultationFields,
  getSectionKeysWithMissingRequiredFields,
  mergeStructuredConsultationFields,
  normalizeStructuredConsultationFieldValueForStorage,
  StructuredConsultationField,
  validateStructuredConsultationFieldValue,
} from '../consultationUtils';
import { ConsultationAddFieldPanel } from './ConsultationAddFieldPanel';
import { ConsultationCoreFieldsForm } from './ConsultationCoreFieldsForm';
import { ConsultationFieldInput } from './ConsultationFieldInput';
import { ConsultationStickyActionBar } from './ConsultationStickyActionBar';
import { ConsultationStructuredFieldsPanel } from './ConsultationStructuredFieldsPanel';
import shellStyles from './ConsultationRecordShell.module.css';
import styles from './CreateConsultationPage.module.css';

const buildWindow = () => {
  const now = new Date();
  const from = new Date(now);
  from.setDate(now.getDate() - 30);
  const to = new Date(now);
  to.setDate(now.getDate() + 90);
  return {
    fromUtc: from.toISOString(),
    toUtc: to.toISOString(),
  };
};

export const CreateConsultationPage: React.FC = () => {
  const { t } = useTranslation(['consultations', 'calendar', 'common', 'assets']);
  const { showToast } = useToast();
  const { hasPermission } = useAuth();
  const canAssignConsultationFields = hasPermission(PermissionScopes.consultationsFieldAssignmentsManage);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialQueryValuesRef = React.useRef({
    assetId: searchParams.get('assetId') ?? '',
    contactId: searchParams.get('contactId') ?? '',
    eventId: searchParams.get('eventId') ?? '',
    appointmentTypeId: searchParams.get('appointmentTypeId') ?? '',
    templateId: searchParams.get('templateId') ?? '',
  });

  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [assets, setAssets] = React.useState<Asset[]>([]);
  const [contacts, setContacts] = React.useState<Contact[]>([]);
  const [events, setEvents] = React.useState<CalendarEvent[]>([]);
  const [appointmentTypes, setAppointmentTypes] = React.useState<AppointmentType[]>([]);
  const [consultationStatuses, setConsultationStatuses] = React.useState<ConsultationStatusDefinition[]>([]);
  const [templates, setTemplates] = React.useState<ConsultationTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = React.useState<ConsultationTemplate | null>(null);
  const [fieldDefinitions, setFieldDefinitions] = React.useState<ConsultationFieldDefinition[]>([]);
  const [pendingFieldAssignments, setPendingFieldAssignments] = React.useState<AddConsultationFieldAssignmentInput[]>([]);
  const [fields, setFields] = React.useState<StructuredConsultationField[]>([]);
  const [fieldValues, setFieldValues] = React.useState<Record<string, string>>({});
  const [assetId, setAssetId] = React.useState('');
  const [contactId, setContactId] = React.useState('');
  const [eventId, setEventId] = React.useState('');
  const [appointmentTypeId, setAppointmentTypeId] = React.useState('');
  const [templateId, setTemplateId] = React.useState('');
  const [status, setStatus] = React.useState('');
  const [summary, setSummary] = React.useState('');
  const [notes, setNotes] = React.useState('');
  const [error, setError] = React.useState('');
  const [forcedActiveSectionKey, setForcedActiveSectionKey] = React.useState<string | null>(null);
  const [highlightedFieldIds, setHighlightedFieldIds] = React.useState<ReadonlySet<string>>(new Set());
  const [redirectTo, setRedirectTo] = React.useState<string | null>(null);
  const [pendingAttachmentFiles, setPendingAttachmentFiles] = React.useState<Record<string, File[]>>({});
  const [pendingVoiceFiles, setPendingVoiceFiles] = React.useState<Record<string, { file: File; durationSeconds?: number }>>({});
  const leaveAfterCreateRef = React.useRef(false);

  React.useEffect(() => {
    let cancelled = false;
    const initialQueryValues = initialQueryValuesRef.current;

    const load = async () => {
      setLoading(true);
      try {
        const windowQuery = buildWindow();
        const [loadedAssets, loadedContacts, loadedEvents, loadedAppointmentTypes, loadedConsultationStatuses, loadedFieldDefinitions] = await Promise.all([
          getAssets({ page: 1, pageSize: 200, sortBy: 'name', sortDir: 'asc' }),
          getContacts(),
          listEvents(windowQuery),
          listConsultationTypes({ status: 'active' }),
          listConsultationStatuses(),
          listConsultationFieldDefinitions({ isActive: true }),
        ]);

        if (cancelled) return;

        setAssets(loadedAssets);
        setContacts(loadedContacts);
        setEvents(loadedEvents);
        setAppointmentTypes(loadedAppointmentTypes);
        setConsultationStatuses(loadedConsultationStatuses);
        setFieldDefinitions(loadedFieldDefinitions);
        setStatus((current) => current || loadedConsultationStatuses.find((item) => item.isDefault)?.name || '');

        const matchedEvent = initialQueryValues.eventId
          ? loadedEvents.find((item) => item.id === initialQueryValues.eventId) ?? null
          : null;
        const matchedEventAssetIds = matchedEvent
          ? matchedEvent.assetIds && matchedEvent.assetIds.length > 0
            ? matchedEvent.assetIds
            : matchedEvent.assetId
              ? [matchedEvent.assetId]
              : []
          : [];

        const resolvedAssetId = initialQueryValues.assetId
          || matchedEventAssetIds[0]
          || '';

        if (resolvedAssetId && loadedAssets.some((item) => item.id === resolvedAssetId)) {
          setAssetId(resolvedAssetId);
        }

        if (
          initialQueryValues.eventId
          && matchedEvent
          && (!resolvedAssetId || matchedEventAssetIds.includes(resolvedAssetId))
        ) {
          setEventId(initialQueryValues.eventId);
        }

        const resolvedContactId = initialQueryValues.contactId
          || matchedEvent?.contactId
          || '';

        if (resolvedContactId && loadedContacts.some((item) => item.id === resolvedContactId)) {
          setContactId(resolvedContactId);
        }

        const resolvedAppointmentTypeId = initialQueryValues.appointmentTypeId
          || matchedEvent?.appointmentTypeId
          || '';

        if (resolvedAppointmentTypeId && loadedAppointmentTypes.some((item) => item.id === resolvedAppointmentTypeId)) {
          setAppointmentTypeId(resolvedAppointmentTypeId);
        }

        if (initialQueryValues.templateId) {
          setTemplateId(initialQueryValues.templateId);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, []);

  React.useEffect(() => {
    if (!appointmentTypeId) {
      setTemplates([]);
      setTemplateId('');
      setSelectedTemplate(null);
      setPendingFieldAssignments([]);
      return;
    }

    let cancelled = false;

    const loadTemplatesByType = async () => {
      const loadedTemplates = await listConsultationTemplates({
        isActive: true,
        appointmentTypeId,
      });

      if (cancelled) {
        return;
      }

      setTemplates(loadedTemplates);
      if (!loadedTemplates.some((item) => item.id === templateId)) {
        setTemplateId('');
      }
    };

    void loadTemplatesByType();

    return () => {
      cancelled = true;
    };
  }, [appointmentTypeId, templateId]);

  React.useEffect(() => {
    if (!templateId) {
      setSelectedTemplate(null);
      return;
    }

    let cancelled = false;

    const loadTemplate = async () => {
      const loadedTemplate = await getConsultationTemplateById(templateId);
      if (cancelled) return;
      setSelectedTemplate(loadedTemplate);
    };

    void loadTemplate();

    return () => {
      cancelled = true;
    };
  }, [templateId]);

  React.useEffect(() => {
    const mergedFields = mergeStructuredConsultationFields(selectedTemplate, fieldDefinitions, pendingFieldAssignments);
    setFields(mergedFields);
    setFieldValues((current) => {
      const next = buildStructuredConsultationFieldValues(mergedFields);
      mergedFields.forEach((field) => {
        if (current[field.id] !== undefined) {
          next[field.id] = current[field.id];
        }
      });
      return next;
    });
  }, [selectedTemplate, fieldDefinitions, pendingFieldAssignments]);

  const handleAddPendingField = React.useCallback((fieldDefinitionId: string, isRequired: boolean) => {
    const maxSortOrder = fields.reduce((max, field) => Math.max(max, field.assignmentSortOrder), 0);
    setPendingFieldAssignments((current) => [
      ...current,
      {
        fieldDefinitionId,
        isRequired,
        sortOrder: maxSortOrder + 1,
      },
    ]);
  }, [fields]);

  const handleRemoveConsultationOnlyField = React.useCallback((fieldId: string) => {
    setPendingFieldAssignments((current) => current.filter((item) => item.fieldDefinitionId !== fieldId));
    setFieldValues((current) => {
      const next = { ...current };
      delete next[fieldId];
      return next;
    });
    setPendingAttachmentFiles((current) => {
      const next = { ...current };
      delete next[fieldId];
      return next;
    });
    setPendingVoiceFiles((current) => {
      const next = { ...current };
      delete next[fieldId];
      return next;
    });
  }, []);

  const selectedAsset = React.useMemo(() => assets.find((item) => item.id === assetId) ?? null, [assetId, assets]);
  const selectedContact = React.useMemo(() => contacts.find((item) => item.id === contactId) ?? null, [contactId, contacts]);
  const selectedEvent = React.useMemo(() => events.find((item) => item.id === eventId) ?? null, [eventId, events]);
  const scopedEvents = React.useMemo(() => {
    if (!assetId) {
      return events;
    }

    return events.filter((event) => {
      const assetIds = event.assetIds && event.assetIds.length > 0
        ? event.assetIds
        : event.assetId
          ? [event.assetId]
          : [];

      return assetIds.includes(assetId);
    });
  }, [assetId, events]);

  const assetOptions = React.useMemo(
    () => assets.map((asset) => ({ value: asset.id, label: asset.name })),
    [assets],
  );

  const contactOptions = React.useMemo(
    () => contacts.map((contact) => ({ value: contact.id, label: contact.name })),
    [contacts],
  );

  const eventOptions = React.useMemo(
    () => scopedEvents.map((event) => ({
      value: event.id,
      label: `${event.title || t('consultation', { defaultValue: 'Consultation' })} • ${formatDateTime(event.startAtUtc)}`,
    })),
    [scopedEvents, t],
  );

  React.useEffect(() => {
    if (!eventId) {
      return;
    }

    const eventStillAvailable = scopedEvents.some((item) => item.id === eventId);
    if (!eventStillAvailable) {
      setEventId('');
    }
  }, [scopedEvents, eventId]);

  React.useEffect(() => {
    if (!selectedEvent?.contactId) {
      return;
    }

    setContactId((current) => current || selectedEvent.contactId || '');
  }, [selectedEvent]);

  React.useEffect(() => {
    if (!selectedEvent?.appointmentTypeId) {
      return;
    }

    setAppointmentTypeId((current) => current || selectedEvent.appointmentTypeId || '');
  }, [selectedEvent]);

  React.useEffect(() => {
    if (saving || leaveAfterCreateRef.current) {
      return;
    }

    const next = new URLSearchParams();
    const syncValue = (key: string, value: string) => {
      if (value) {
        next.set(key, value);
      }
    };

    syncValue('assetId', assetId);
    syncValue('contactId', contactId);
    syncValue('eventId', eventId);
    syncValue('appointmentTypeId', appointmentTypeId);
    syncValue('templateId', templateId);

    if (next.toString() === searchParams.toString()) {
      return;
    }

    setSearchParams(next, { replace: true });
  }, [appointmentTypeId, assetId, contactId, eventId, saving, searchParams, setSearchParams, templateId]);

  const onFieldChange = React.useCallback((fieldId: string, value: string) => {
    setFieldValues((current) => ({ ...current, [fieldId]: value }));
  }, []);

  const handleCreate = React.useCallback(async () => {
    if (!appointmentTypeId) {
      setError(t('consultationTypeRequired', { defaultValue: 'Consultation type is required.' }));
      return;
    }

    const pendingFieldIds = new Set<string>([
      ...Object.entries(pendingAttachmentFiles)
        .filter(([, files]) => files.length > 0)
        .map(([fieldId]) => fieldId),
      ...Object.keys(pendingVoiceFiles),
    ]);

    const missingRequiredFields = getMissingRequiredConsultationFields(fields, fieldValues, pendingFieldIds);
    if (missingRequiredFields.length > 0) {
      const missingFieldIds = getMissingRequiredConsultationFieldIds(fields, fieldValues, pendingFieldIds);
      setHighlightedFieldIds(new Set(missingFieldIds));
      setForcedActiveSectionKey(getSectionKeysWithMissingRequiredFields(fields, fieldValues, pendingFieldIds)[0] ?? null);
      setError(t('consultationMissingRequiredFields', {
        defaultValue: 'Complete the required structured fields: {{fields}}.',
        fields: missingRequiredFields.join(', '),
      }));
      return;
    }

    for (const field of fields) {
      const validationError = validateStructuredConsultationFieldValue(field, fieldValues[field.id]);
      if (validationError) {
        setHighlightedFieldIds(new Set([field.id]));
        setForcedActiveSectionKey(getSectionKeysWithMissingRequiredFields([field], fieldValues)[0] ?? null);
        setError(t('consultationInvalidStructuredField', {
          defaultValue: '{{field}}: {{message}}.',
          field: field.name,
          message: validationError,
        }));
        return;
      }
    }

    setHighlightedFieldIds(new Set());
    setForcedActiveSectionKey(null);

    setSaving(true);
    setError('');

    try {
      const created = await Promise.race([
        createConsultation({
          assetId: assetId || undefined,
          contactId: contactId || selectedEvent?.contactId || undefined,
          eventId: eventId || undefined,
          appointmentTypeId,
          templateId: templateId || undefined,
          domainProfileKey: selectedTemplate?.domainProfileKey,
          subjectType: selectedTemplate?.subjectType,
          status: status || undefined,
          summary: summary || undefined,
          notes: notes || undefined,
        }),
        new Promise<null>((_, reject) => {
          window.setTimeout(() => {
            reject(new Error(t('consultationCreateTimeout', {
              defaultValue: 'Creating the consultation timed out. Check your connection and try again.',
            })));
          }, 30000);
        }),
      ]);

      if (!created?.id) {
        setError(t('consultationCreateFailed', { defaultValue: 'Failed to create consultation.' }));
        return;
      }

      leaveAfterCreateRef.current = true;
      setRedirectTo(`/consultations/${created.id}`);
      showToast(t('consultationCreateBackgroundUpload', {
        defaultValue: 'Consultation created. Attachments and voice notes may still be uploading in the background.',
      }), 'success');

      void (async () => {
        try {
          for (const assignment of pendingFieldAssignments) {
            await addConsultationFieldAssignment(created.id, assignment);
          }

          for (const field of fields) {
            const value = normalizeStructuredConsultationFieldValueForStorage(field.fieldType, fieldValues[field.id]);
            if (!value) {
              continue;
            }

            if (field.fieldType === 'attachments' || field.fieldType === 'voice') {
              continue;
            }

            await addConsultationFieldValue(created.id, {
              fieldDefinitionId: field.id,
              value,
              sortOrder: field.assignmentSortOrder,
            });
          }

          for (const field of fields.filter((item) => item.fieldType === 'attachments')) {
            const files = pendingAttachmentFiles[field.id] ?? [];
            for (const file of files) {
              await uploadConsultationFieldAttachment(created.id, field.id, file);
            }
          }

          for (const field of fields.filter((item) => item.fieldType === 'voice')) {
            const pendingVoice = pendingVoiceFiles[field.id];
            if (!pendingVoice) {
              continue;
            }

            await uploadConsultationFieldVoice(created.id, field.id, pendingVoice.file, pendingVoice.durationSeconds);

            const voiceSchema = parseConsultationVoiceSchema(field.schemaJson);
            if (voiceSchema?.enableTranscription !== false) {
              void transcribeConsultationFieldVoice(created.id, field.id).catch(() => {
                // Transcription errors are stored on the field value; creation should still succeed.
              });
            }
          }
        } catch {
          // Consultation was created; field persistence can be completed from the details page.
        }
      })();
    } catch (createError) {
      leaveAfterCreateRef.current = false;
      setRedirectTo(null);
      setError(createError instanceof Error
        ? createError.message
        : t('consultationCreateFailed', { defaultValue: 'Failed to create consultation.' }));
    } finally {
      setSaving(false);
    }
  }, [appointmentTypeId, assetId, contactId, eventId, fieldValues, fields, notes, pendingAttachmentFiles, pendingFieldAssignments, pendingVoiceFiles, selectedEvent?.contactId, selectedTemplate?.domainProfileKey, selectedTemplate?.subjectType, showToast, status, summary, t, templateId]);

  if (loading) {
    return (
      <div className={`company-section ${shellStyles.pageSection}`}>
        <Spinner />
      </div>
    );
  }

  if (redirectTo) {
    return <Navigate to={redirectTo} replace />;
  }

  return (
    <div className={styles.page}>
      <Breadcrumb
        items={[
          { label: t('consultations', { defaultValue: 'Consultations' }), path: '/consultations' },
          { label: t('createConsultation', { defaultValue: 'Create consultation' }), path: '/consultations/new' },
        ]}
      />

      <section className={styles.hero}>
        <h1 className={styles.title}>{t('createConsultation', { defaultValue: 'Create consultation' })}</h1>
        <p className={styles.subtitle}>
          {t('createConsultationIntro', { defaultValue: 'Start a new consultation by choosing consultation type and optional linked event, asset, contact, and template.' })}
        </p>
      </section>

      <section className={styles.panel}>
        <h2 className={styles.sectionTitle}>{t('consultationSetup', { defaultValue: 'Setup' })}</h2>
        <div className={styles.grid}>
          <div className={styles.fieldRow}>
            <label className={styles.label}>{t('asset', { ns: 'assets', defaultValue: 'Asset' })}</label>
            <SearchableSelect
              value={assetId}
              onChange={setAssetId}
              options={assetOptions}
              clearOption={{ value: '', label: t('selectAssetOptional', { defaultValue: 'No asset linked' }) }}
              placeholder={t('searchAssets', { defaultValue: 'Search assets…' })}
              noOptionsText={t('noAssetMatches', { defaultValue: 'No matching assets' })}
              inputClassName="text-input"
              ariaLabel={t('asset', { ns: 'assets', defaultValue: 'Asset' })}
            />
          </div>

          <div className={styles.fieldRow}>
            <label className={styles.label}>{t('contact', { defaultValue: 'Contact' })}</label>
            <SearchableSelect
              value={contactId}
              onChange={setContactId}
              options={contactOptions}
              clearOption={{ value: '', label: t('selectContactOptional', { defaultValue: 'No contact linked' }) }}
              placeholder={t('searchContacts', { defaultValue: 'Search contacts…' })}
              noOptionsText={t('noContactMatches', { defaultValue: 'No matching contacts' })}
              inputClassName="text-input"
              ariaLabel={t('contact', { defaultValue: 'Contact' })}
            />
          </div>

          <div className={styles.fieldRow}>
            <label className={styles.label}>{t('linkedEvent', { defaultValue: 'Linked event' })}</label>
            <SearchableSelect
              value={eventId}
              onChange={setEventId}
              options={eventOptions}
              clearOption={{ value: '', label: t('selectEventOptional', { defaultValue: 'No event linked' }) }}
              placeholder={t('searchEvents', { defaultValue: 'Search events…' })}
              noOptionsText={t('noEventMatches', { defaultValue: 'No matching events' })}
              inputClassName="text-input"
              ariaLabel={t('linkedEvent', { defaultValue: 'Linked event' })}
            />
            <p className={styles.hint}>{t('createConsultationEventHint', { defaultValue: 'Showing recent and upcoming calendar events.' })}</p>
          </div>

          <div className={styles.fieldRow}>
            <label className={styles.label}>{t('consultationType', { defaultValue: 'Consultation type' })}</label>
            <select className="text-input" value={appointmentTypeId} onChange={(event) => setAppointmentTypeId(event.target.value)}>
              <option value="">{t('selectConsultationType', { defaultValue: 'Select consultation type' })}</option>
              {appointmentTypes.map((type) => (
                <option key={type.id} value={type.id}>{type.name}</option>
              ))}
            </select>
          </div>

          <div className={styles.fieldRow}>
            <label className={styles.label}>{t('template', { defaultValue: 'Template' })}</label>
            <select className="text-input" value={templateId} onChange={(event) => setTemplateId(event.target.value)} disabled={!appointmentTypeId}>
              <option value="">{t('selectTemplateOptional', { defaultValue: 'No template' })}</option>
              {templates.map((template) => (
                <option key={template.id} value={template.id}>{template.name}</option>
              ))}
            </select>
          </div>
        </div>

        <div className={styles.summaryCard}>
          <div>
            <span className={styles.summaryLabel}>{t('asset', { ns: 'assets', defaultValue: 'Asset' })}</span>
            <span className={styles.summaryValue}>{selectedAsset?.name || '-'}</span>
          </div>
          <div>
            <span className={styles.summaryLabel}>{t('contact', { defaultValue: 'Contact' })}</span>
            <span className={styles.summaryValue}>{selectedContact?.name || '-'}</span>
          </div>
          <div>
            <span className={styles.summaryLabel}>{t('linkedEvent', { defaultValue: 'Linked event' })}</span>
            <span className={styles.summaryValue}>{selectedEvent ? `${selectedEvent.title || t('consultation', { defaultValue: 'Consultation' })} • ${formatDateTime(selectedEvent.startAtUtc)}` : '-'}</span>
          </div>
          <div>
            <span className={styles.summaryLabel}>{t('consultationType', { defaultValue: 'Consultation type' })}</span>
            <span className={styles.summaryValue}>{appointmentTypes.find((item) => item.id === appointmentTypeId)?.name || '-'}</span>
          </div>
          <div>
            <span className={styles.summaryLabel}>{t('template', { defaultValue: 'Template' })}</span>
            <span className={styles.summaryValue}>{selectedTemplate?.name || '-'}</span>
          </div>
        </div>
      </section>

      <ConsultationCoreFieldsForm
        status={status}
        summary={summary}
        notes={notes}
        availableStatuses={consultationStatuses}
        onStatusChange={setStatus}
        onSummaryChange={setSummary}
        onNotesChange={setNotes}
      />

      <section className={shellStyles.panel}>
        <h2 className={shellStyles.sectionTitle}>{t('structuredConsultationFields', { defaultValue: 'Structured fields' })}</h2>
        {fields.length === 0 ? (
          <p className={shellStyles.empty}>
            {t('consultationNoStructuredFieldsYet', {
              defaultValue: 'No structured fields yet. Add consultation-only fields below or choose a template that includes fields.',
            })}
          </p>
        ) : (
          <ConsultationStructuredFieldsPanel
            fields={fields}
            fieldValues={fieldValues}
            onFieldChange={onFieldChange}
            canRemoveConsultationOnlyFields={canAssignConsultationFields}
            onRemoveConsultationOnlyField={handleRemoveConsultationOnlyField}
            sessionStorageKey={templateId ? `consultation-create-section:${templateId}` : 'consultation-create-section:draft'}
            highlightedFieldIds={highlightedFieldIds}
            forcedActiveSectionKey={forcedActiveSectionKey}
            renderFieldInput={(field) => (
              <ConsultationFieldInput
                field={field}
                value={fieldValues[field.id] ?? ''}
                onChange={(value) => onFieldChange(field.id, value)}
                onPendingAttachmentsChange={(files) => {
                  setPendingAttachmentFiles((current) => ({
                    ...current,
                    [field.id]: files,
                  }));
                }}
                onPendingVoiceChange={(file, durationSeconds) => {
                  setPendingVoiceFiles((current) => {
                    const next = { ...current };
                    if (!file) {
                      delete next[field.id];
                      return next;
                    }

                    next[field.id] = { file, durationSeconds };
                    return next;
                  });
                }}
              />
            )}
          />
        )}
        <ConsultationAddFieldPanel
          disabled={!canAssignConsultationFields}
          fieldDefinitions={fieldDefinitions}
          fields={fields}
          onAdd={handleAddPendingField}
        />
      </section>

      <ConsultationStickyActionBar statusError={error}>
        <button className="btn btn-secondary" onClick={() => navigate('/consultations')}>
          {t('cancel', { ns: 'common', defaultValue: 'Cancel' })}
        </button>
        <button className="btn btn-primary" onClick={() => void handleCreate()} disabled={saving}>
          {saving ? t('saving', { ns: 'common', defaultValue: 'Saving…' }) : t('createConsultation', { defaultValue: 'Create consultation' })}
        </button>
      </ConsultationStickyActionBar>
    </div>
  );
};

export default CreateConsultationPage;
